import { useFocusEffect } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSocket } from 'context/SocketContext';
import { useMobileOrDesktop } from 'hoc/mobileOrDesktop';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, AppStateStatus, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { heroStyle } from './ProgramScreen';

import Button from 'components/Button';
import Hero from 'components/Hero';
import Player from 'components/Player';
import SpallaPlayerPlatform from 'components/Player/SpallaPlayer/SpallaPlayerPlatform';
import { useStream } from 'components/Player/Stream/context';
import { usePlayer } from 'components/Player/context';
import ProgramContent from 'components/Program/Content';
import ProgramHeader from 'components/Program/Header';
import ProgramRelated from 'components/Program/Related';
import ProgramSave from 'components/Program/Save';
import ProgramShared from 'components/Program/Shared';
import { useProgram } from 'components/Program/context';
import IsLoading from 'components/isLoading';

const MovieScreen: React.FC<RootStackScreenProps<'Program'>> = ({ navigation }) => {
  const {
    isPlaying,
    isFullScreen,
    isStreaming,
    pause,
    play,
    setIsVisible,
    mute,
    setNextTrack,
    media,
    lastWatchedMedia,
    lastWatchedMediaTime,
    setLastWatchedMedia,
    setLastWatchedMediaTime,
  } = usePlayer();

  const program = useProgram();
  const { watch } = useSocket();
  const analytics = useAnalytics();
  const { ads, hasInternet, prevHasInternetRef } = useGlobalConfig();
  const { isConnected } = useStream();
  const [isActive, setIsActive] = useState(false);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [mediaId, setMediaId] = useState<string>();
  const [startTime, setStartTime] = useState<number>();
  const [appState, setAppState] = useState(AppState.currentState);
  const buttonRef = useRef<TouchableOpacity>(null);
  const { selectedProfile } = useSelectedProfile();
  const isUnder18 = selectedProfile?.ageRating !== '18';

  const canPlay = isActive && mediaId !== undefined;

  useEffect(() => {
    setNextTrack(undefined);
  }, []);

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      if (isPlaying && !isStreaming) {
        pause();
      }
      mute(false);
    });
  }, [navigation, isPlaying, isStreaming, pause]);

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        if (!isStreaming) pause();
        setIsActive(false);
      };
    }, [isStreaming])
  );

  useFocusEffect(
    useCallback(() => {
      analytics.triggerMovieScreenPageViewEvent({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category.map((tag) => tag.name).join(', '),
        programProducer: program.ads?.adunit?.name,
        programDirector: program.director,
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        programYear: program.year,
      });
    }, [])
  );

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        if (Platform.isAndroidTV && buttonRef?.current) {
          buttonRef?.current?.requestTVFocus();
        }
      }
      setAppState(nextAppState);
    });

    return () => subscription.remove();
  }, [appState]);

  const playerProps: PlayerProps | undefined = useMemo(() => {
    if (!program) {
      return undefined;
    }
    const mediaSource = program.mediaSource;

    const media: PlayerMedia = {
      title: program.title,
      image: [program.imageVertical?.url!, program.imageHorizontal?.url!],
      duration: program.duration,
      programId: program._id,
      programTitle: program.title,
      programUrl: program.url,
      ratingValue: program.rating,
      ratingDescription: program.rateDescription,
      videoTracks: [],
      mediaSource,
    };

    const mediaIdDub = program.mediaIdDub;
    const mediaIdDubSpalla = program.videoIdDub;

    if (mediaIdDub) {
      media.videoTracks.push({
        language: 'dub',
        trackId: mediaSource ? mediaIdDubSpalla : mediaIdDub,
      });
    }

    media.videoTracks.push({
      language: 'origin',
      trackId: mediaSource ? program.videoId : program.mediaId,
    });

    return {
      media,
      isLive: program.type === ProgramType.LIVE || program.type === ProgramType.RADIO,
      adTagUrl:
        ads?.disableAds || program.ads?.disableAds || isUnder18
          ? undefined
          : Platform.isWebTV
          ? ads?.ctvVast
          : modifyVastUrl(ads?.vast, program),
      adsExpirationTime: ads?.adsExpirationTime,
    };
  }, [program, isConnected]);

  const loadWatchedTime = useCallback(
    async (props?: PlayerProps) => {
      if (!props || props.isLive) return;

      const data = await watch(props);

      if (lastWatchedMedia && lastWatchedMedia.programId !== playerProps?.media?.programId) return;

      if (data?.watchedTime != null) {
        setStartTime(data.watchedTime);
      }

      setIsDataReady(true);
    },
    [watch]
  );

  useEffect(() => {
    if (lastWatchedMedia && media && lastWatchedMedia.programId !== media.programId) {
      setLastWatchedMedia(null);
      setLastWatchedMediaTime(null);
    }
  }, [media, lastWatchedMedia]);

  useEffect(() => {
    if (!hasInternet) {
      pause();
      setIsDataReady(false);
      if (media) {
        setLastWatchedMedia(media);
      }
      return;
    }

    if (lastWatchedMedia && media && lastWatchedMedia.programId !== media.programId) {
      setLastWatchedMedia(null);
      setLastWatchedMediaTime(null);
      loadWatchedTime(playerProps);
      return;
    }

    if (!lastWatchedMedia) {
      loadWatchedTime(playerProps);
    }
  }, [hasInternet, playerProps, loadWatchedTime, lastWatchedMedia, media]);

  useEffect(() => {
    const prev = prevHasInternetRef.current;
    prevHasInternetRef.current = hasInternet;

    if (prev !== false || hasInternet !== true) return;

    if (!lastWatchedMedia || !media) return;

    if (lastWatchedMedia.programId !== media.programId) {
      setLastWatchedMedia(null);
      setLastWatchedMediaTime(null);
      return;
    }

    setMediaId(program.mediaId || program.videoId);
    setStartTime(lastWatchedMediaTime ?? undefined);
    setIsDataReady(true);

    setLastWatchedMedia(null);
    setLastWatchedMediaTime(null);
  }, [hasInternet, lastWatchedMedia, lastWatchedMediaTime, media, program]);

  useEffect(() => {
    if (mediaId !== undefined && isDataReady) {
      setIsActive(true);
    }
  }, [mediaId, isDataReady]);

  const Video =
    canPlay && playerProps ? (
      <Player {...playerProps} startTime={startTime} autoplay={!Platform.isWebTV} />
    ) : null;

  const [VideoMobile, VideoDesktop] = useMobileOrDesktop(Video, Video);

  useEffect(() => {
    if (!mediaId) setMediaId(program.mediaId || program.videoId);
  }, [program, mediaId]);

  return (
    <>
      {(Platform.isAndroidTV || Platform.OS === 'web' || !isFullScreen) && (
        <Hero program={program} hideCover={mediaId !== undefined}>
          {VideoMobile !== undefined && (
            <SpallaPlayerPlatform
              videoId={program.videoId}
              videoIdDub={program.videoIdDub}
              playerDuration={program.duration}
              startTime={startTime}
              autoplay={true}
            />
          )}

          <View style={heroStyle.container}>
            <Hero.Header
              program={program}
              mediaId={mediaId}
              Aside={
                VideoDesktop !== undefined && !Platform.isAndroidTV ? (
                  <View style={styles.videoBox}>
                    {Platform.isWebTV ? (
                      <>{Video}</>
                    ) : (
                      <SpallaPlayerPlatform
                        videoId={program.videoId}
                        videoIdDub={program.videoIdDub}
                        playerDuration={program.duration}
                        startTime={startTime}
                        autoplay={true}
                      />
                    )}
                  </View>
                ) : undefined
              }>
              <ProgramHeader />
              {!isDataReady ? (
                <View style={styles.loadingContainer}>
                  <IsLoading />
                </View>
              ) : (
                <Button
                  id="PlayBtn"
                  ref={buttonRef}
                  useTVFocus
                  hasTVPreferredFocus
                  style={{
                    ...(Platform.OS === 'web' || Platform.isTV
                      ? {
                          width: calcSizeForDevice(300, 400),
                          display: mediaId && !Platform.isTV ? 'none' : undefined,
                          height: calcSizeForDevice(40, 52),
                        }
                      : {
                          display: mediaId && !Platform.isTV ? 'none' : undefined,
                          height: 40,
                          fontFamily: 'system-ui',
                        }),
                  }}
                  textStyle={{ fontSize: calcSizeForDevice(18, 24) }}
                  iconStyle={{
                    width: calcSizeForDevice(24, 30),
                    height: calcSizeForDevice(24, 30),
                  }}
                  iconType={Platform.OS !== 'web' && !Platform.isTV ? 'Play' : undefined}
                  variant="gradient"
                  size="md"
                  isFullWidth={Platform.OS !== 'web' && !Platform.isTV}
                  onPress={() => {
                    analytics.triggerMediaPlayActionEvent({
                      programId: program.id,
                      programTitle: program.title,
                      programAgeRating: program.rating,
                      programAdUnit: program.ads?.adunit?.val!,
                      programCategory: program.category?.map((tag) => tag.name).join(', '),
                      programProducer: program.ads?.adunit?.name,
                      programDirector: program.director,
                      programGenre: program.genre.map((genre) => genre.name).join(', '),
                      programYear: program.year,
                    });

                    play();

                    if (Platform.isTV) setIsVisible(true);
                  }}>
                  {startTime
                    ? 'Continuar assistindo'
                    : Platform.OS !== 'web'
                    ? 'Assistir'
                    : 'Assistir agora'}
                </Button>
              )}
              <View style={styles.actionsContainer}>
                <ProgramSave />
                <ProgramShared />
              </View>
              <ProgramContent />
            </Hero.Header>

            <View style={styles.relatedContainer}>
              <ProgramRelated />
            </View>
          </View>
        </Hero>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  videoBox: {
    aspectRatio: 1 / 0.56,
    width: Platform.isTV ? 0 : '80%',
    overflow: 'hidden',
  },
  loadingContainer: {
    width: calcSizeForDevice(300, 400),
    height: calcSizeForDevice(40, 52),
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: calcSizeForDevice(10, 14),
    marginBottom: calcSizeForDevice(10, 20),
    gap: 8, // Equivalente ao space="md"
  },
  relatedContainer: {
    paddingVertical: calcSizeForDevice(24),
  },
  loadingContainerSpalla: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000000',
    position: 'absolute',
    zIndex: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default MovieScreen;
